import { useEffect, useState } from 'react';
import { APP_NAME, pageTitle } from '@/lib/appConfig';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Package, 
  Users, 
  Building2, 
  ShoppingCart, 
  TrendingUp, 
  Search, 
  Clipboard,
  DollarSign,
  ArrowUpRight,
  ArrowDownRight,
  Eye,
  Plus,
  Activity,
  Clock,
  CheckCircle,
  AlertCircle,
  Sparkles,
  BarChart3,
  CreditCard,
  Layers,
  Store
} from 'lucide-react';
import { fetchDashboardMetrics } from '@/api/dashboard';
import { toast } from 'sonner';
import { XAxis, YAxis, Tooltip, ResponsiveContainer, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';
import { useSystemTimezone } from '@/hooks/use-system-timezone';

interface DashboardStats {
  totalItems: number;
  totalInventoryCost: number;
  totalUsers: number;
  totalBranches: number;
  todaySales: number;
  monthlySales: number;
  todayTransactions?: number;
  recentSales: { description: string; time: string }[];
  branchName?: string;
  salesTrend?: {
    date: string;
    label: string;
    sales: number;
    creditPayments: number;
    total: number;
  }[];
  monthlyBreakdown?: {
    sales: number;
    creditPayments: number;
  };
  inventoryHealth?: {
    healthy: number;
    lowStock: number;
    outOfStock: number;
  };
  branchSalesBreakdown?: {
    branch_id: string;
    branch_name: string;
    amount: number;
  }[];
  layawaySummary?: {
    todayLayawayPayments: number;
    monthlyLayawayRevenue: number;
    totalActiveLayaways: number;
  };
}

export default function Dashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const currencySymbol = useCurrencySymbol();
  const systemTimezone = useSystemTimezone();

  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [greeting, setGreeting] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = pageTitle('Dashboard');
  }, []);

  useEffect(() => {
    const hour = new Date().getHours();
    if (hour < 12) setGreeting('Good Morning');
    else if (hour < 18) setGreeting('Good Afternoon');
    else setGreeting('Good Evening');
  }, []);

  useEffect(() => {
    fetchDashboardMetrics()
      .then((data) => {
        setStats(data);
        setLoading(false);
      })
      .catch((err) => {
        toast.error('Failed to load dashboard metrics');
        setError('Failed to load dashboard metrics');
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }
  
  if (error || !stats) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-500">{error}</p>
        </div>
      </div>
    );
  }

  const currentTime = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', timeZone: systemTimezone });
  const currentDate = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: systemTimezone,
  });
  const salesTrend = stats.salesTrend?.length
    ? stats.salesTrend
    : stats.recentSales.map((s, i) => ({
        date: String(i),
        label: `#${i + 1}`,
        sales: (() => {
          const match = s.description.match(/([0-9]+(?:[,][0-9]{3})*(?:\.[0-9]{2})?)/);
          return match ? parseFloat(match[1].replace(/,/g, '')) : 0;
        })(),
        creditPayments: 0,
        total: (() => {
          const match = s.description.match(/([0-9]+(?:[,][0-9]{3})*(?:\.[0-9]{2})?)/);
          return match ? parseFloat(match[1].replace(/,/g, '')) : 0;
        })(),
      }));
  const monthlyMix = [
    { name: 'Sales', value: stats.monthlyBreakdown?.sales ?? stats.monthlySales, color: '#2563eb' },
    { name: 'Credit Payments', value: stats.monthlyBreakdown?.creditPayments ?? 0, color: '#16a34a' },
  ].filter(entry => entry.value > 0);
  const inventoryHealth = [
    { name: 'Healthy', value: stats.inventoryHealth?.healthy ?? 0, color: '#16a34a' },
    { name: 'Low Stock', value: stats.inventoryHealth?.lowStock ?? 0, color: '#f59e0b' },
    { name: 'Out', value: stats.inventoryHealth?.outOfStock ?? 0, color: '#dc2626' },
  ];
  const averageDailyRevenue = salesTrend.length
    ? salesTrend.reduce((sum, day) => sum + Number(day.total || 0), 0) / salesTrend.length
    : 0;

  return (
    <div className="space-y-8 animate-in fade-in-50 duration-500">
      {/* Header Section */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-blue-600 via-purple-600 to-blue-800 p-8 text-white">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative z-10">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Sparkles className="h-6 w-6 text-yellow-300" />
                <h1 className="text-3xl lg:text-4xl font-bold">
                  {greeting}, {user?.name}!
                </h1>
              </div>
              <p className="text-blue-100 text-lg">
                {isAdmin ? `Managing your entire ${APP_NAME} system` : `Working at ${stats.branchName}`}
              </p>
              <div className="flex items-center space-x-4 text-sm text-blue-200">
                <div className="flex items-center space-x-1">
                  <Clock className="h-4 w-4" />
                  <span>{currentTime}</span>
                </div>
                <span>•</span>
                <span>{currentDate}</span>
              </div>
            </div>
            <div className="mt-6 lg:mt-0 flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
              <Link to="/sales">
                <Button className="w-full sm:w-auto bg-white/90 hover:bg-white text-blue-700 hover:text-blue-800 border-white shadow-lg font-semibold">
                  <Plus className="h-4 w-4 mr-2" />
                  New Sale
                </Button>
              </Link>
              <Link to="/inventory">
                <Button className="w-full sm:w-auto bg-white/10 hover:bg-white/20 text-white border-white/50 hover:border-white backdrop-blur-sm font-semibold">
                  <Package className="h-4 w-4 mr-2" />
                  Inventory
                </Button>
              </Link>
            </div>
          </div>
        </div>
        <div className="absolute -top-4 -right-4 h-24 w-24 rounded-full bg-white/10"></div>
        <div className="absolute -bottom-8 -left-8 h-32 w-32 rounded-full bg-white/5"></div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Link to="/inventory" className="group">
          <Card className="relative overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-blue-900/20 dark:to-indigo-900/20">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="p-2 bg-blue-500 rounded-lg">
                  <Package className="h-6 w-6 text-white" />
                </div>
                <ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:text-blue-600 transition-colors" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-1">
                <h3 className="font-semibold text-blue-900 dark:text-blue-100">Total Inventory</h3>
                <div className="text-3xl font-bold text-blue-600">{stats.totalItems}</div>
                <p className="text-sm text-blue-600/70">Items in stock</p>
                <div className="flex items-center space-x-2 mt-2">
                  <DollarSign className="h-3 w-3 text-green-600" />
                  <span className="text-xs font-medium text-green-600">
                    {currencySymbol}{stats.totalInventoryCost.toFixed(2)} total value
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to={`/reports?tab=daily-sales&date=${new Date().toLocaleDateString('en-CA', { timeZone: systemTimezone })}`} className="group">
          <Card className="relative overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-gradient-to-br from-green-50 to-emerald-100 dark:from-green-900/20 dark:to-emerald-900/20">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="p-2 bg-green-500 rounded-lg">
                  <ShoppingCart className="h-6 w-6 text-white" />
                </div>
                <Badge className="bg-green-100 text-green-700 hover:bg-green-200">
                  Today
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-1">
                <h3 className="font-semibold text-green-900 dark:text-green-100">Today's Sales</h3>
                <div className="text-3xl font-bold text-green-600">{currencySymbol}{stats.todaySales.toFixed(2)}</div>
                <p className="text-sm text-green-600/70">Open today&apos;s EOD report</p>
                <div className="flex items-center space-x-1 mt-2">
                  <TrendingUp className="h-3 w-3 text-green-600" />
                  <span className="text-xs text-green-600">Cash, bank, and credit sale payments today</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/reports" className="group">
          <Card className="relative overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-gradient-to-br from-purple-50 to-violet-100 dark:from-purple-900/20 dark:to-violet-900/20">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="p-2 bg-purple-500 rounded-lg">
                  <DollarSign className="h-6 w-6 text-white" />
                </div>
                <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-200">
                  MTD
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-1">
                <h3 className="font-semibold text-purple-900 dark:text-purple-100">Monthly Sales</h3>
                <div className="text-3xl font-bold text-purple-600">{currencySymbol}{stats.monthlySales.toFixed(2)}</div>
                <p className="text-sm text-purple-600/70">Revenue this month</p>
                <div className="flex items-center space-x-1 mt-2">
                  <TrendingUp className="h-3 w-3 text-purple-600" />
                  <span className="text-xs text-purple-600">Growing steadily</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>

        {isAdmin ? (
          <Link to="/users" className="group">
            <Card className="relative overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-gradient-to-br from-orange-50 to-red-100 dark:from-orange-900/20 dark:to-red-900/20">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="p-2 bg-orange-500 rounded-lg">
                    <Users className="h-6 w-6 text-white" />
                  </div>
                  <CheckCircle className="h-4 w-4 text-green-600" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-1">
                  <h3 className="font-semibold text-orange-900 dark:text-orange-100">Active Users</h3>
                  <div className="text-3xl font-bold text-orange-600">{stats.totalUsers}</div>
                  <p className="text-sm text-orange-600/70">Team members</p>
                  <div className="flex items-center space-x-1 mt-2">
                    <Building2 className="h-3 w-3 text-orange-600" />
                    <span className="text-xs text-orange-600">{stats.totalBranches} branches</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        ) : (
          <Link to="/expenses" className="group">
            <Card className="relative overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-gradient-to-br from-orange-50 to-red-100 dark:from-orange-900/20 dark:to-red-900/20">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="p-2 bg-orange-500 rounded-lg">
                    <Clipboard className="h-6 w-6 text-white" />
                  </div>
                  <ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:text-orange-600 transition-colors" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-1">
                  <h3 className="font-semibold text-orange-900 dark:text-orange-100">Expenses</h3>
                  <div className="text-3xl font-bold text-orange-600">View</div>
                  <p className="text-sm text-orange-600/70">Manage expenses</p>
                </div>
              </CardContent>
            </Card>
          </Link>
        )}
      </div>

      {/* Operational Snapshot */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Transactions Today</p>
                <p className="mt-1 text-2xl font-bold">{stats.todayTransactions ?? 0}</p>
              </div>
              <div className="rounded-lg bg-blue-50 p-2 text-blue-600">
                <ShoppingCart className="h-5 w-5" />
              </div>
            </div>
            <p className="mt-3 text-xs text-muted-foreground">Sales plus credit sale payments</p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">7-Day Avg Revenue</p>
                <p className="mt-1 text-2xl font-bold">{currencySymbol}{averageDailyRevenue.toFixed(2)}</p>
              </div>
              <div className="rounded-lg bg-emerald-50 p-2 text-emerald-600">
                <BarChart3 className="h-5 w-5" />
              </div>
            </div>
            <p className="mt-3 text-xs text-muted-foreground">Based on latest daily revenue trend</p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active Credit Sales</p>
                <p className="mt-1 text-2xl font-bold">{stats.layawaySummary?.totalActiveLayaways ?? 0}</p>
              </div>
              <div className="rounded-lg bg-purple-50 p-2 text-purple-600">
                <CreditCard className="h-5 w-5" />
              </div>
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              {stats.layawaySummary?.todayLayawayPayments ?? 0} payment(s) received today
            </p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Stock Alerts</p>
                <p className="mt-1 text-2xl font-bold">
                  {(stats.inventoryHealth?.lowStock ?? 0) + (stats.inventoryHealth?.outOfStock ?? 0)}
                </p>
              </div>
              <div className="rounded-lg bg-amber-50 p-2 text-amber-600">
                <Layers className="h-5 w-5" />
              </div>
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              {stats.inventoryHealth?.lowStock ?? 0} low stock, {stats.inventoryHealth?.outOfStock ?? 0} out of stock
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts and Activity */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Sales Trend Chart */}
        <div className="xl:col-span-2">
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg font-semibold">7-Day Revenue Trend</CardTitle>
                  <CardDescription>Sales and credit sale payments by day</CardDescription>
                </div>
                <Button variant="outline" size="sm" asChild>
                  <Link to="/reports">
                    <Eye className="h-4 w-4 mr-2" />
                    View Details
                  </Link>
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={salesTrend}>
                  <defs>
                    <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="label" axisLine={false} tickLine={false} />
                  <YAxis axisLine={false} tickLine={false} />
                  <Tooltip 
                    formatter={(value: number | string) => `${currencySymbol}${Number(value).toFixed(2)}`}
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: 'none', 
                      borderRadius: '8px', 
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' 
                    }} 
                  />
                  <Area 
                    type="monotone" 
                    dataKey="total" 
                    name="Total"
                    stroke="#3B82F6" 
                    strokeWidth={2}
                    fill="url(#salesGradient)" 
                  />
                  <Area
                    type="monotone"
                    dataKey="creditPayments"
                    name="Credit Payments"
                    stroke="#16A34A"
                    strokeWidth={2}
                    fill="transparent"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Recent Activity */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg font-semibold">Recent Activity</CardTitle>
                <CardDescription>Latest transactions</CardDescription>
              </div>
              <Activity className="h-5 w-5 text-blue-600" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {stats.recentSales.slice(0, 5).map((activity, idx) => (
                <div key={idx} className="flex items-start space-x-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50">
                  <div className="p-1.5 bg-green-100 dark:bg-green-900/30 rounded-full">
                    <CheckCircle className="h-3 w-3 text-green-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {activity.description.replace('$', currencySymbol)}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {activity.time}
                    </p>
                  </div>
                </div>
              ))}
              {stats.recentSales.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Activity className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No recent activity</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Deeper Insights */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Monthly Revenue Mix</CardTitle>
            <CardDescription>Regular sales vs credit sale payments</CardDescription>
          </CardHeader>
          <CardContent>
            {monthlyMix.length ? (
              <div className="grid grid-cols-1 gap-4">
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={monthlyMix} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={3}>
                      {monthlyMix.map((entry) => (
                        <Cell key={entry.name} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value: number | string) => `${currencySymbol}${Number(value).toFixed(2)}`} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-2">
                  {monthlyMix.map(entry => (
                    <div key={entry.name} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: entry.color }} />
                        <span>{entry.name}</span>
                      </div>
                      <span className="font-semibold">{currencySymbol}{entry.value.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex h-[220px] items-center justify-center text-sm text-muted-foreground">
                No monthly revenue yet
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Inventory Health</CardTitle>
            <CardDescription>Stock status by inventory record</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={inventoryHealth}>
                <XAxis dataKey="name" axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} axisLine={false} tickLine={false} />
                <Tooltip />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                  {inventoryHealth.map(entry => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Top Branches</CardTitle>
            <CardDescription>Month-to-date revenue by branch</CardDescription>
          </CardHeader>
          <CardContent>
            {isAdmin && stats.branchSalesBreakdown?.length ? (
              <div className="space-y-4">
                {stats.branchSalesBreakdown.map((branch, index) => {
                  const maxAmount = Math.max(...(stats.branchSalesBreakdown || []).map(item => item.amount), 1);
                  const width = Math.max(8, (branch.amount / maxAmount) * 100);
                  return (
                    <div key={branch.branch_id} className="space-y-2">
                      <div className="flex items-center justify-between gap-3 text-sm">
                        <div className="flex min-w-0 items-center gap-2">
                          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-50 text-xs font-semibold text-blue-700">
                            {index + 1}
                          </span>
                          <span className="truncate font-medium">{branch.branch_name}</span>
                        </div>
                        <span className="font-semibold">{currencySymbol}{branch.amount.toFixed(2)}</span>
                      </div>
                      <div className="h-2 rounded-full bg-slate-100">
                        <div className="h-2 rounded-full bg-blue-600" style={{ width: `${width}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex h-[220px] flex-col items-center justify-center text-center text-sm text-muted-foreground">
                <Store className="mb-2 h-8 w-8 opacity-50" />
                {isAdmin ? 'No branch sales this month' : stats.branchName || 'Branch view'}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className="border-0 shadow-lg bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900">
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Quick Actions</CardTitle>
          <CardDescription>Common tasks to get you started</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Link to="/sales">
              <Button className="w-full h-auto p-4 bg-blue-600 hover:bg-blue-700 flex flex-col items-center space-y-2">
                <Plus className="h-6 w-6" />
                <span className="text-sm font-medium">New Sale</span>
              </Button>
            </Link>
            <Link to="/inventory">
              <Button variant="outline" className="w-full h-auto p-4 flex flex-col items-center space-y-2">
                <Package className="h-6 w-6" />
                <span className="text-sm font-medium">Add Inventory</span>
              </Button>
            </Link>
            <Link to="/expenses">
              <Button variant="outline" className="w-full h-auto p-4 flex flex-col items-center space-y-2">
                <Clipboard className="h-6 w-6" />
                <span className="text-sm font-medium">Log Expense</span>
              </Button>
            </Link>
            <Link to="/reports">
              <Button variant="outline" className="w-full h-auto p-4 flex flex-col items-center space-y-2">
                <TrendingUp className="h-6 w-6" />
                <span className="text-sm font-medium">View Reports</span>
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
